import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import {
    decodePlaceForwardOrderResponse,
    decodePlacePendingOrderResponse,
    decodePlaceSpotOrderResponse,
    encodePlaceForwardOrderRequest,
    encodePlacePendingOrderRequest,
    encodePlaceSpotOrderRequest,
    timeoutReject,
} from './place-order-adapter.utils';
import { filter, finalize, map, share, take, takeUntil, tap, timeoutWith } from 'rxjs/operators';
import {
    IForwardOrderRequest,
    IPendingOrderRequestWithClient,
    IPlaceOrderIdResponse,
    ISpotOrderRequestWithClient,
} from '../../../interfaces';
import { inject, injectable } from 'inversify';
import { IPlaceOrderAdapter } from './place-order-adapter.interface';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { merge, Observable, of } from 'rxjs';
import { SERVER_TIMEOUT } from '../../../helpers';

@injectable()
export class PlaceOrderAdapterService implements IPlaceOrderAdapter {
    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    private _placeOrdersFeed$: Observable<IPlaceOrderIdResponse>;

    private readonly placeOrdersFeed = (): Observable<IPlaceOrderIdResponse> =>
        this._placeOrdersFeed$ ?? (this._placeOrdersFeed$ = merge(
            this.communicationService.dataObservable(gateway.MessageTypes.PlaceSpotOrder)
                .pipe(map(decodePlaceSpotOrderResponse)),
            this.communicationService.dataObservable(gateway.MessageTypes.PlaceForwardOrder)
                .pipe(map(decodePlaceForwardOrderResponse)),
            this.communicationService.dataObservable(gateway.MessageTypes.PlacePendingOrder)
                .pipe(map(decodePlacePendingOrderResponse)),
        ).pipe(
            takeUntil(this.sessionLifeCycle.destroyObservable()),
            finalize(() => this.clean()),
            share(),
        ));

    placeSpotOrder(placeOrderRequest: ISpotOrderRequestWithClient): Promise<IPlaceOrderIdResponse> {

        AlkLogger.info(`[PlaceOrderAdapterService#placeSpotOrder] is about to place ${ JSON.stringify(placeOrderRequest) } `);

        const requestId = this.communicationService
            .create(
                encodePlaceSpotOrderRequest(placeOrderRequest),
                gateway.MessageTypes.PlaceSpotOrder,
            );

        return this.internalPlaceOrder(requestId);
    }

    placeForwardOrder(placeOrderRequest: IForwardOrderRequest): Promise<IPlaceOrderIdResponse> {

        AlkLogger.info(`[PlaceOrderAdapterService#placeForwardOrder] is about to place ${ JSON.stringify(placeOrderRequest) } `);

        const requestId = this.communicationService
            .create(
                encodePlaceForwardOrderRequest(placeOrderRequest),
                gateway.MessageTypes.PlaceForwardOrder,
            );

        return this.internalPlaceOrder(requestId);
    }

    placePendingOrder(placeOrderRequest: IPendingOrderRequestWithClient): Promise<IPlaceOrderIdResponse> {

        AlkLogger.info(`[PlaceOrderAdapterService#placePendingOrder] is about to place ${ JSON.stringify(placeOrderRequest) } `);

        const requestId = this.communicationService
            .create(
                encodePlacePendingOrderRequest(placeOrderRequest),
                gateway.MessageTypes.PlacePendingOrder,
            );

        return this.internalPlaceOrder(requestId);
    }

    private readonly internalPlaceOrder = (requestId: number): Promise<IPlaceOrderIdResponse> =>
        this.placeOrdersFeed().pipe(
            timeoutWith<IPlaceOrderIdResponse, IPlaceOrderIdResponse>(
                SERVER_TIMEOUT,
                of(timeoutReject(requestId)),
            ),
            filter(response => response.requestId === requestId),
            finalize(this.delete(requestId)),
            tap((request) =>
                AlkLogger.info(`[PlaceOrderAdapterService#internalPlaceOrder] response for order ${ ''
                }request ${ requestId } is ${ JSON.stringify(request) }`)),
            take(1),
        ).toPromise();

    private readonly delete = (token: number) => () => {
        this.communicationService.delete(token);
    };

    private clean(): void {
        this._placeOrdersFeed$ = null;
    }
}
